import { useEffect } from 'react';
import { Form, Field, TextInput, Checkbox, useFormApi, useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { ReferenceIdField, DateField } from '@/components/common';
import { declarationDetailsValidation } from '../../allocationsValidation';

/**
 * Props:
 *   name      the form key
 *   label     the field's own label
 *   required  marks it and adds the required rule
 *   disabled  locks the input while another control owns its value
 */
const TextField = ({ name, label, required = false, disabled = false }) => (
  <Field name={name} required={required}>
    {({ id, value, onChange, onBlur, status, message }) => (
      <TextInput
        id={id}
        name={name}
        label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        status={status}
        message={message}
        required={required}
        disabled={disabled}
      />
    )}
  </Field>
);

/**
 * Props:
 *   name    the checkbox's own form key
 *   label   the checkbox's own label
 *   source  the address it copies from
 *   target  the address it copies into, and keeps in step while ticked
 */
const SameAsAddressField = ({ name, label, source, target }) => {
  const { values } = useFormState();
  const { setValue } = useFormApi();
  const isChecked = Boolean(values?.[name]);
  const sourceValue = values?.[source] ?? '';

  // Ticked means "track it", not "copy once": editing the source carries straight over.
  useEffect(() => {
    if (isChecked) setValue(target, sourceValue);
  }, [isChecked, sourceValue, target, setValue]);

  return (
    <Field name={name} type="checkbox">
      {({ id, checked, onChange }) => (
        <Checkbox id={id} name={name} label={label} checked={checked} onChange={onChange} />
      )}
    </Field>
  );
};

/**
 * Props:
 *   supplierCertificateLocked  the server supplied the supplier's certificate number, so it is read-only
 */
export const DeclarationDetailsStep = ({ supplierCertificateLocked = false }) => {
  const t = useTranslations();
  const { values } = useFormState();
  // A copied address is neither editable nor required: the field it copies carries both.
  const isDispatchSameAsSupplier = Boolean(values?.dispatchSameAsSupplier);
  const isReceiptSameAsRecipient = Boolean(values?.receiptSameAsRecipient);

  return (
    <>
      <Form.Section title={t('SECTION_SUSTAINABILITY_DECLARATION_DETAILS')}>
        <Form.Row columns={1}>
          <ReferenceIdField
            label={t('ALLOCATIONS_FIELD_SD_NUMBER')}
            rules={declarationDetailsValidation.idNumber}
          />
        </Form.Row>

        <Form.Row columns={2}>
          <DateField
            fieldName="dateOfIssuance"
            label={t('ALLOCATIONS_FIELD_DATE_OF_ISSUANCE')}
            required
          />
          <DateField fieldName="dateOfDispatch" label={t('FIELD_DATE_OF_DISPATCH')} required />
        </Form.Row>
      </Form.Section>

      <Form.Row columns={2}>
        <Form.Section title={t('SECTION_SUPPLIER')}>
          <TextField name="supplierName" label={t('FIELD_NAME')} required />
          <TextField name="supplierAddress" label={t('FIELD_ADDRESS')} required />
          <TextField
            name="supplierCertificateNumber"
            label={t('ALLOCATIONS_FIELD_CERTIFICATE_NUMBER')}
            required={!supplierCertificateLocked}
            disabled={supplierCertificateLocked}
          />
          <TextField
            name="supplierDispatchAddress"
            label={t('ALLOCATIONS_FIELD_ADDRESS_OF_DISPATCH')}
            required={!isDispatchSameAsSupplier}
            disabled={isDispatchSameAsSupplier}
          />
          <SameAsAddressField
            name="dispatchSameAsSupplier"
            label={t('ALLOCATIONS_SAME_AS_SUPPLIER_ADDRESS')}
            source="supplierAddress"
            target="supplierDispatchAddress"
          />
        </Form.Section>

        <Form.Section title={t('SECTION_RECIPIENT')}>
          <TextField name="recipientName" label={t('FIELD_NAME')} required />
          <TextField name="recipientAddress" label={t('FIELD_ADDRESS')} required />
          <TextField
            name="contractNumber"
            label={t('ALLOCATIONS_FIELD_CONTRACT_NUMBER')}
            required
          />
          <TextField
            name="recipientReceiptAddress"
            label={t('ALLOCATIONS_FIELD_ADDRESS_OF_RECEIPT')}
            required={!isReceiptSameAsRecipient}
            disabled={isReceiptSameAsRecipient}
          />
          <SameAsAddressField
            name="receiptSameAsRecipient"
            label={t('ALLOCATIONS_SAME_AS_RECIPIENT_ADDRESS')}
            source="recipientAddress"
            target="recipientReceiptAddress"
          />
        </Form.Section>
      </Form.Row>
    </>
  );
};
